import { adminAuditSchema } from "@rosc/contracts";
import { Button } from "@rosc/ui/components/button";
import { Input } from "@rosc/ui/components/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@rosc/ui/components/pagination";
import { Skeleton } from "@rosc/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@rosc/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { adminKeys } from "@/lib/admin-queries";
import { referenceClient } from "@/lib/orpc/client";
import { useAuthStore } from "@/store/auth.store";

export function AdminAudit() {
  const uid = useAuthStore((state) => state.user?.uid);
  const [actor, setActor] = useState("");
  const [target, setTarget] = useState("");
  const [applied, setApplied] = useState<{ actor?: string; target?: string }>(
    {},
  );
  const [cursors, setCursors] = useState<string[]>([]);
  const filters = { ...applied, cursor: cursors.at(-1), limit: 25 };
  const query = useQuery({
    queryKey: adminKeys(uid ?? "").audit(filters),
    queryFn: async ({ signal }) =>
      adminAuditSchema.parse(
        await referenceClient.adminAudit(
          { query: filters },
          { signal, context: { expectedUserUid: uid } },
        ),
      ),
    enabled: !!uid,
    retry: false,
  });
  return (
    <section className="space-y-4" aria-labelledby="admin-audit-title">
      <h2 id="admin-audit-title" className="text-xl font-semibold">
        Audit log
      </h2>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          setApplied({
            actor: actor.trim() || undefined,
            target: target.trim() || undefined,
          });
          setCursors([]);
        }}
      >
        <label htmlFor="audit-actor" className="min-w-0 space-y-1">
          <span>Actor email</span>
          <Input
            id="audit-actor"
            type="email"
            maxLength={255}
            value={actor}
            onChange={(event) => setActor(event.target.value)}
          />
        </label>
        <label htmlFor="audit-target" className="min-w-0 space-y-1">
          <span>Target public ID</span>
          <Input
            id="audit-target"
            maxLength={36}
            value={target}
            onChange={(event) => setTarget(event.target.value)}
          />
        </label>
        <Button type="submit">Apply filters</Button>
      </form>
      {query.isPending ? (
        <Skeleton className="h-64 w-full" aria-label="Loading audit log" />
      ) : query.isError ? (
        <div role="alert">
          {"code" in query.error && query.error.code === "FORBIDDEN" ? (
            "Not authorized"
          ) : (
            <>
              Unable to load audit log.{" "}
              <Button onClick={() => void query.refetch()}>Try again</Button>
            </>
          )}
        </div>
      ) : query.data.items.length === 0 ? (
        <p role="status">No audit entries found.</p>
      ) : (
        <Table>
          <TableCaption>Newest audit entries first.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Target public ID</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Outcome</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.data.items.map((entry) => (
              <TableRow key={entry.auditId}>
                <TableCell>{entry.createdAt}</TableCell>
                <TableCell>
                  {entry.actorEmail ??
                    (entry.actorKind === "bootstrap"
                      ? "Bootstrap"
                      : "Unavailable")}
                </TableCell>
                <TableCell>{entry.action}</TableCell>
                <TableCell>{entry.publicId ?? "None"}</TableCell>
                <TableCell className="max-w-sm whitespace-normal break-words">
                  {entry.reason ?? "None"}
                </TableCell>
                <TableCell>{entry.outcome}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Pagination aria-label="Audit pages">
        <PaginationContent>
          <PaginationItem>
            <Button
              variant="outline"
              disabled={!cursors.length || query.isFetching}
              onClick={() => setCursors((previous) => previous.slice(0, -1))}
            >
              Previous
            </Button>
          </PaginationItem>
          <PaginationItem>
            <span className="px-3" aria-live="polite">
              Page {cursors.length + 1}
            </span>
          </PaginationItem>
          <PaginationItem>
            <Button
              variant="outline"
              disabled={
                query.isError || query.isFetching || !query.data?.nextCursor
              }
              onClick={() => {
                const next = query.data?.nextCursor;
                if (next) setCursors((previous) => [...previous, next]);
              }}
            >
              Next
            </Button>
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </section>
  );
}
